// Copyright (c) 2025, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useEffect, useState } from 'react';
import { Button, Box } from '@mui/material';
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';
import './GoogleDrive.css';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useGetApi } from '../../../data/hooks/api';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import MovieIcon from '@mui/icons-material/Movie';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import CodeIcon from '@mui/icons-material/Code';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ArticleIcon from '@mui/icons-material/Article';

interface GoogleDriveProps {
  driveLocation: string;
}

interface File {
  id: string;
  name: string;
  mimeType: string;
}

interface PathButtonProps {
  text?: string;
  icon?: React.ReactNode;
  onClick: () => void;
  tooltip: string;
  active?: boolean;
}

// Functional component that renders a navigational button for breadcrumb paths
const PathButton: React.FC<PathButtonProps> = ({ text, icon, onClick, tooltip, active }) => (
  <Button
    className={`path-button ${active ? 'active' : ''}`} // Apply "active" class if button represents current folder
    onClick={onClick} // Click handler to navigate to the folder
    title={tooltip} // Tooltip displaying folder name or description
    startIcon={icon} // Optionally display an icon before the text
  >
    {text} {/* Display button label */}
  </Button>
);

// Utility function to get the appropriate icon based on file type
const getFileIcon = (mimeType: string, name: string) => {
  // Check for Google Drive specific types first
  if (mimeType === 'application/vnd.google-apps.folder') {
    return <FolderIcon sx={{ verticalAlign: 'middle', marginRight: 1, color: '#FFA726' }} />;
  }
  if (mimeType === 'application/vnd.google-apps.document') {
    return <DescriptionIcon sx={{ verticalAlign: 'middle', marginRight: 1, color: '#4285F4' }} />;
  }
  if (mimeType === 'application/vnd.google-apps.spreadsheet') {
    return <TableChartIcon sx={{ verticalAlign: 'middle', marginRight: 1, color: '#0F9D58' }} />;
  }

  // Check file extensions for regular files
  const extension = name.toLowerCase().split('.').pop();
  switch (extension) {
    case 'pdf':
      return <PictureAsPdfIcon sx={{ verticalAlign: 'middle', marginRight: 1, color: '#FF5252' }} />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
      return <ImageIcon sx={{ verticalAlign: 'middle', marginRight: 1, color: '#4CAF50' }} />;
    case 'mp4':
    case 'avi':
    case 'mov':
    case 'wmv':
      return <MovieIcon sx={{ verticalAlign: 'middle', marginRight: 1, color: '#FF4081' }} />;
    case 'mp3':
    case 'wav':
    case 'ogg':
      return <AudiotrackIcon sx={{ verticalAlign: 'middle', marginRight: 1, color: '#7C4DFF' }} />;
    case 'js':
    case 'ts':
    case 'java':
    case 'py':
    case 'cpp':
    case 'c':
    case 'h':
    case 'cs':
    case 'php':
      return <CodeIcon sx={{ verticalAlign: 'middle', marginRight: 1, color: '#FFC107' }} />;
    default:
      return <ArticleIcon sx={{ verticalAlign: 'middle', marginRight: 1, color: '#757575' }} />;
  }
};

// Utility function to extract folder ID from Google Drive URL
const extractFolderIdFromUrl = (url: string): string | null => {
  if (!url) return null;
  
  try {
    // Handle both URL formats:
    // https://drive.google.com/drive/folders/FOLDER_ID
    // https://drive.google.com/drive/u/0/folders/FOLDER_ID
    const match = url.match(/\/folders\/([^/?]+)/);
    return match ? match[1] : null;
  } catch (error) {
    console.error('Error extracting folder ID from URL:', error);
    return null;
  }
};

// Main GoogleDrive component that handles folder navigation and file display
export const GoogleDrive: React.FC<GoogleDriveProps> = ({ driveLocation }) => {
  const [files, setFiles] = useState<File[]>([]); // Stores the list of files in the selected folder
  const [path, setPath] = useState<{ id: string; name: string }[]>([]); // Stores the breadcrumb navigation history
  const [currentFolderId, setCurrentFolderId] = useState<string>(''); // Stores the ID of the currently viewed folder
  const [folderNotFound, setFolderNotFound] = useState<boolean>(false); // Tracks whether the requested folder was found
  const [isError, setIsError] = useState<boolean>(false); // Tracks if an error occurred during fetching

  const backendUrl = window.config.asgardeoConfig.resourceServerURLs[0];
  const fileViewBaseUrl = window.config.googleDriveConfig.fileViewUrl;
  const folderViewBaseUrl = window.config.googleDriveConfig.folderViewUrl;

  // Use useGetApi for fetching folder contents
  const { data: filesData, loading: filesLoading, error: filesError, getApiData: fetchFilesApi } = useGetApi<File[]>({
    url: `${backendUrl}/files`,
    headers: { accept: 'application/json' },
  });

  // Function to fetch contents of a folder using its folder ID
  const fetchFolder = async (folderId: string) => {
    try {
      const url = new URL(`${backendUrl}/files`);
      url.searchParams.append('folderId', folderId);
      await fetchFilesApi(url.toString());
      setCurrentFolderId(folderId);
      setFolderNotFound(false);
      setIsError(false);
    } catch (error) {
      console.error('Error fetching folder contents:', error);
      setIsError(true);
    }
  };

  // Handle fetched files data
  useEffect(() => {
    if (filesData) {
      setFiles(filesData);
      setIsError(false);
    }
    if (filesError) {
      setIsError(true);
    }
  }, [filesData, filesError]);

  // Function to handle clicks on files or folders
  const handleClick = (file: File) => {
    if (file.mimeType === 'application/vnd.google-apps.folder') { // Check if clicked item is a folder
      const folderExistsInPath = path.some((p) => p.id === file.id); // Verify if folder is already in breadcrumb path
      if (!folderExistsInPath) {
        setPath([...path, { id: file.id, name: file.name }]); // Add folder to breadcrumb navigation
      }
      fetchFolder(file.id); // Fetch contents of the clicked folder
      setCurrentFolderId(file.id); // Update current folder ID
    } else {
      openFile(file.id); // Open file in a new tab if it's not a folder
    }
  };

  // Function to handle breadcrumb navigation clicks
  const handleBreadcrumbClick = (index: number) => {
    const newPath = path.slice(0, index + 1); // Retrieve clicked folder's path history
    setPath(newPath); // Update navigation path
    const selectedFolderId = newPath[index].id; // Retrieve folder ID of clicked breadcrumb
    fetchFolder(selectedFolderId); // Fetch contents of the selected folder
    setCurrentFolderId(selectedFolderId); // Update current folder ID
  };

  // Function to open a file in a new browser tab
  const openFile = (fileId: string) => {
    const url = `${fileViewBaseUrl}${fileId}/view`; // Generate Google Drive file view link
    window.open(url, '_blank'); // Open file in a new tab
  };

  // Function to open the current folder in Google Drive
  const goToCurrentFolder = () => {
    if (currentFolderId) {
      const url = `${folderViewBaseUrl}${currentFolderId}`; // Generate folder link
      window.open(url, '_blank'); // Open folder in a new tab
    } else {
      console.error('No folder selected or invalid currentFolderId'); // Log error for missing folder ID
    }
  };

  // Initialize folder when component mounts
  useEffect(() => {
    const folderId = extractFolderIdFromUrl(driveLocation);
    if (folderId) {
      fetchFolder(folderId);
    } else {
      setFolderNotFound(true);
    }
  }, [driveLocation]);

  return (
    <div className="google-drive">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <div className="breadcrumb">
          <PathButton
            text={folderNotFound ? 'No Folder Found' : filesLoading ? 'Loading...' : 'Root'}
            onClick={() => { 
              setPath([]);
              const folderId = extractFolderIdFromUrl(driveLocation);
              if (folderId) fetchFolder(folderId);
            }}
            tooltip="Back to root"
          />
          {path.length > 0 && // Render breadcrumb buttons only if there are folders in the navigation path
            path.map((p, index) => ( // Loop through each folder in the breadcrumb path
              <React.Fragment key={index}> {/* Fragment to group elements without adding extra DOM nodes */}
                
                <ArrowForwardIos // Breadcrumb separator arrow icon
                  fontSize="small"
                  sx={{ margin: '0 8px', verticalAlign: 'middle', marginTop: '8px' }} // Styling for proper spacing
                />
                <PathButton
                  text={p.name} // Folder name in the breadcrumb
                  onClick={() => handleBreadcrumbClick(index)} // Handle breadcrumb navigation when clicked
                  tooltip={p.name} // Display folder name as tooltip
                  active={index === path.length - 1} // Highlight if it's the last folder in the path
                />
              </React.Fragment>
            ))}
        </div>

        {!folderNotFound && path.length > 0 && ( // Show button to open the current folder in Google Drive if folder exists
          <Button
            variant="contained"
            color="primary"
            onClick={goToCurrentFolder} // Open the current folder in Google Drive
            sx={{ minWidth: '4px', padding: '4px', color: '#fff', marginTop: '-8px' }} // Styling for the button
          >
            <OpenInNewIcon sx={{ fontSize: '20px' }} />
          </Button>
        )}
      </Box>

      {filesLoading ? (
        <div className="loading-message">
          <p>Loading folder contents...</p>
        </div>
      ) : isError ? ( // If there was an error fetching data, show an error message
        <div className="error-message">
          <p>An error occurred while fetching data. Please try again.</p>
          <Button 
            variant="contained" 
            color="secondary" 
            onClick={() => {
              const folderId = extractFolderIdFromUrl(driveLocation);
              if (folderId) fetchFolder(folderId);
            }}
          >
            Retry
          </Button>
        </div>
      ) : folderNotFound ? ( // If no folder was found, show the appropriate error message
        <div className="error-message">
          <p>No folder found at the specified drive location.</p>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => {
              const folderId = extractFolderIdFromUrl(driveLocation);
              if (folderId) fetchFolder(folderId);
            }}
          >
            Refresh
          </Button>
        </div>
      ) : files.length === 0 ? ( // If the folder is empty, display an appropriate message
        <div className="empty-folder-message">
          <p>This folder is currently empty.</p>
        </div>
      ) : ( // If files exist, render them inside a table
        <table className="file-table"> {/* Container for file listing */}
          <thead> {/* Table header */}
            <tr>
              <th style={{ cursor: 'default' }}>Name</th> {/* Column for file/folder name */}
            </tr>
          </thead>
          <tbody> {/* Table body */}
            {files
              .slice() // Create a shallow copy of the array
              .sort((a, b) => {
                // First, sort by type (folders first)
                const aIsFolder = a.mimeType === 'application/vnd.google-apps.folder';
                const bIsFolder = b.mimeType === 'application/vnd.google-apps.folder';
                
                if (aIsFolder && !bIsFolder) return -1;
                if (!aIsFolder && bIsFolder) return 1;
                
                // If both are folders or both are files, sort alphabetically
                return a.name.localeCompare(b.name, undefined, { numeric: true });
              })
              .map((file) => ( // Map each file to a table row
                <tr 
                  key={file.id} 
                  onClick={() => handleClick(file)}
                  style={{ cursor: 'pointer' }}
                > 
                  <td>
                    {getFileIcon(file.mimeType, file.name)}
                    <span style={{ verticalAlign: 'middle' }}>{file.name}</span>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
